import React, { useState, useMemo } from "react";
import { UploadCloud, CheckCircle2, X, AlertTriangle, Download } from "lucide-react";
import { api } from "../services/api";
import { generateVoterPassword } from "../utils/voterAuth";
import { parseCsvText, autoMatchColumns, ColumnMapping } from "../utils/csvHelper";

interface CsvImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  electionId: string;
  electionTitle: string;
  onImportSuccess: (
    result: { count: number; newAdded?: number; updated?: number; totalAccredited?: number; message?: string },
    importedVoters?: any[]
  ) => void;
}

export const CsvImportModal: React.FC<CsvImportModalProps> = ({
  isOpen,
  onClose,
  electionId,
  electionTitle,
  onImportSuccess,
}) => {
  const [csvRawHeaders, setCsvRawHeaders] = useState<string[]>([]);
  const [csvRawRows, setCsvRawRows] = useState<string[][]>([]);
  const [csvFileName, setCsvFileName] = useState("");
  const [csvFileRawText, setCsvFileRawText] = useState("");
  const [hasHeaderRow, setHasHeaderRow] = useState(true);
  const [showDuplicateDrawer, setShowDuplicateDrawer] = useState(false);
  const [csvImportMode, setCsvImportMode] = useState<"all" | "custom">("all");
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({
    matric: -1,
    name: -1,
    email: -1,
    password: -1,
    level: -1,
    gender: -1,
    hall: -1,
    department: -1,
  });
  const [autoGenPasswords, setAutoGenPasswords] = useState(true);
  const [importingVoters, setImportingVoters] = useState(false);
  const [importProgress, setImportProgress] = useState<{ current: number; total: number } | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importSuccessState, setImportSuccessState] = useState<{
    result: any;
    voters: any[];
  } | null>(null);

  const resetState = () => {
    setCsvRawHeaders([]);
    setCsvRawRows([]);
    setCsvFileName("");
    setCsvFileRawText("");
    setHasHeaderRow(true);
    setShowDuplicateDrawer(false);
    setCsvImportMode("all");
    setAutoGenPasswords(true);
    setImportProgress(null);
    setImportError(null);
    setImportSuccessState(null);
    setColumnMapping({
      matric: -1,
      name: -1,
      email: -1,
      password: -1,
      level: -1,
      gender: -1,
      hall: -1,
      department: -1,
    });
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const applyCsvParse = (text: string, withHeaders: boolean) => {
    setImportError(null);
    const { headers, rows } = parseCsvText(text, { hasHeaderRow: withHeaders });
    if (headers.length === 0 || rows.length === 0) {
      alert("CSV file is empty or missing data rows");
      return;
    }

    const mapping = autoMatchColumns(headers, rows[0]);
    setCsvRawHeaders(headers);
    setCsvRawRows(rows);
    setColumnMapping(mapping);
    setHasHeaderRow(withHeaders);
  };

  const handleCsvFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFileName(file.name);
    setImportError(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      if (!text) return;
      setCsvFileRawText(text);

      const peek = parseCsvText(text);
      const initialWithHeaders = peek.detectedHasHeaders;
      applyCsvParse(text, initialWithHeaders);

      setCsvImportMode("all");
      setAutoGenPasswords(true);
    };
    reader.readAsText(file);
  };

  const handleToggleHeaders = (checked: boolean) => {
    if (!csvFileRawText) return;
    applyCsvParse(csvFileRawText, checked);
  };

  const csvParsedVoters = useMemo(() => {
    if (csvRawRows.length === 0) return [];
    return csvRawRows
      .map((row) => {
        const matric = columnMapping.matric !== -1 ? row[columnMapping.matric]?.trim() : "";
        const name = columnMapping.name !== -1 ? row[columnMapping.name]?.trim() : "";
        if (!matric || !name) return null;

        const email =
          columnMapping.email !== -1 && row[columnMapping.email]?.trim()
            ? row[columnMapping.email].trim()
            : `${matric}@student.ui.edu.ng`;

        let password = "";
        if (!autoGenPasswords && columnMapping.password !== -1 && row[columnMapping.password]?.trim()) {
          password = row[columnMapping.password].trim();
        } else {
          password = generateVoterPassword();
        }

        const rawLevel =
          columnMapping.level !== -1 && row[columnMapping.level]?.trim()
            ? row[columnMapping.level].trim().replace(/[^0-9]/g, "")
            : "100";
        const level = ["100", "200", "300", "400", "500"].includes(rawLevel) ? rawLevel : "100";

        const gender =
          columnMapping.gender !== -1 && row[columnMapping.gender]?.trim()
            ? row[columnMapping.gender].trim()
            : "Unspecified";

        const hall =
          columnMapping.hall !== -1 && row[columnMapping.hall]?.trim()
            ? row[columnMapping.hall].trim()
            : "Not Specified";

        const department =
          columnMapping.department !== -1 && row[columnMapping.department]?.trim()
            ? row[columnMapping.department].trim()
            : "Not Specified";

        return {
          matric,
          name,
          email,
          password,
          level,
          gender,
          hall,
          department,
        };
      })
      .filter((v): v is NonNullable<typeof v> => v !== null);
  }, [csvRawRows, columnMapping, autoGenPasswords]);

  const { uniqueVoters, duplicateGroups, totalDuplicatesDropped } = useMemo(() => {
    if (csvParsedVoters.length === 0) {
      return { uniqueVoters: [], duplicateGroups: [], totalDuplicatesDropped: 0 };
    }
    const map = new Map<string, (typeof csvParsedVoters)[0]>();
    const dupMap = new Map<string, { rowIdx: number; name: string; email: string }[]>();

    csvParsedVoters.forEach((voter, idx) => {
      const key = voter.matric.trim().toLowerCase();
      if (!dupMap.has(key)) {
        dupMap.set(key, [{ rowIdx: idx + 1, name: voter.name, email: voter.email }]);
        map.set(key, voter);
      } else {
        dupMap.get(key)!.push({ rowIdx: idx + 1, name: voter.name, email: voter.email });
        map.set(key, voter);
      }
    });

    const duplicates: { matric: string; count: number; rows: { rowIdx: number; name: string; email: string }[] }[] = [];
    let duplicatesDropped = 0;
    for (const [matric, items] of dupMap.entries()) {
      if (items.length > 1) {
        duplicates.push({ matric, count: items.length, rows: items });
        duplicatesDropped += items.length - 1;
      }
    }

    return {
      uniqueVoters: Array.from(map.values()),
      duplicateGroups: duplicates,
      totalDuplicatesDropped: duplicatesDropped,
    };
  }, [csvParsedVoters]);

  const downloadBatchCsv = (votersToExport: any[], customFilename?: string) => {
    let csv = "Matric No,Student Name,Email,Voting Password,Academic Level,Gender,Hall of Residence,Department\n";
    votersToExport.forEach((v) => {
      csv += `"${v.matric}","${v.name}","${v.email}","${v.password || ''}","${v.level}L","${v.gender || 'Unspecified'}","${v.hall || 'Not Specified'}","${v.department || 'Not Specified'}"\n`;
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", customFilename || `accredited-voters-batch-${votersToExport.length}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleCommitCsv = async () => {
    if (uniqueVoters.length === 0) return;
    setImportError(null);
    setImportingVoters(true);
    setImportProgress({ current: 0, total: uniqueVoters.length });
    const res = await api.batchImportVoters(electionId, uniqueVoters, (imported, total) => {
      setImportProgress({ current: imported, total });
    });
    setImportingVoters(false);
    setImportProgress(null);

    if (res.success) {
      onImportSuccess(res, uniqueVoters);
      setImportSuccessState({ result: res, voters: uniqueVoters });
    } else {
      setImportError(res.error || "Failed to import voters. Please check file format or server connection.");
    }
  };

  if (!isOpen) return null;

  if (importSuccessState) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.85)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 100,
          padding: "20px",
        }}
      >
        <div
          style={{
            backgroundColor: "#171619",
            border: "1px solid #2D2C31",
            borderRadius: "14px",
            width: "100%",
            maxWidth: "540px",
            padding: "28px",
            display: "flex",
            flexDirection: "column",
            gap: "18px",
            textAlign: "center",
            boxShadow: "0 20px 40px rgba(0,0,0,0.6)",
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "50%",
              backgroundColor: "rgba(16, 185, 129, 0.15)",
              border: "1.5px solid #10B981",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto",
            }}
          >
            <CheckCircle2 size={32} color="#10B981" />
          </div>

          <div>
            <h3 style={{ fontSize: "20px", fontWeight: 800, color: "#FFFFFF", margin: "0 0 6px 0" }}>
              Batch Import Completed!
            </h3>
            <p style={{ color: "#A1A1AA", fontSize: "13px", lineHeight: 1.5, margin: 0 }}>
              {importSuccessState.result.message || `Successfully accredited ${importSuccessState.voters.length} voters for ${electionTitle}.`}
            </p>
          </div>

          <div
            style={{
              backgroundColor: "#0E0D0F",
              border: "1px solid #2D2C31",
              borderRadius: "10px",
              padding: "14px 18px",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "12px",
              textAlign: "left",
            }}
          >
            <div>
              <div style={{ fontSize: "11px", color: "#8E8E93", fontWeight: 700, textTransform: "uppercase" }}>
                New Voters Added
              </div>
              <div style={{ fontSize: "20px", fontWeight: 800, color: "#10B981", marginTop: "2px" }}>
                {importSuccessState.result.newAdded ?? importSuccessState.voters.length}
              </div>
            </div>
            <div>
              <div style={{ fontSize: "11px", color: "#8E8E93", fontWeight: 700, textTransform: "uppercase" }}>
                Existing Refreshed
              </div>
              <div style={{ fontSize: "20px", fontWeight: 800, color: "#FF9900", marginTop: "2px" }}>
                {importSuccessState.result.updated ?? 0}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "6px" }}>
            <button
              onClick={() =>
                downloadBatchCsv(
                  importSuccessState.voters,
                  `newly-added-credentials-${importSuccessState.voters.length}-voters.csv`
                )
              }
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                padding: "12px 18px",
                borderRadius: "8px",
                backgroundColor: "#FF9900",
                color: "#171619",
                border: "none",
                fontSize: "14px",
                fontWeight: 800,
                cursor: "pointer",
                boxShadow: "0 4px 15px rgba(255, 153, 0, 0.25)",
              }}
            >
              <Download size={17} />
              Export Just This Batch's Credentials ({importSuccessState.voters.length} Voters)
            </button>

            <button
              onClick={handleClose}
              style={{
                padding: "10px",
                borderRadius: "8px",
                backgroundColor: "#27272A",
                color: "#E4E4E7",
                border: "1px solid #3F3F46",
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Done & Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
        padding: "20px",
      }}
    >
      <div
        style={{
          backgroundColor: "#171619",
          border: "1px solid #2D2C31",
          borderRadius: "14px",
          width: "100%",
          maxWidth: "840px",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "22px 28px 18px 28px", borderBottom: "1px solid #2D2C31", flexShrink: 0, position: "relative" }}>
          <button
            onClick={handleClose}
            style={{
              position: "absolute",
              right: "20px",
              top: "20px",
              background: "transparent",
              border: "none",
              color: "#8E8E93",
              cursor: "pointer",
            }}
          >
            <X size={20} />
          </button>

          <h3 style={{ fontSize: "18px", fontWeight: 800, margin: "0 0 6px 0", color: "#FFFFFF" }}>
            Batch Import Accredited Voters (CSV)
          </h3>
          <p style={{ color: "#8E8E93", fontSize: "13px", margin: 0 }}>
            Upload your student voter registry into {electionTitle}. The system auto-matches columns or lets you pick attributes, and generates secure 7-digit access codes.
          </p>
        </div>

        <div style={{ padding: "20px 28px", overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: "16px" }}>

        {csvRawRows.length === 0 ? (
          <div
            style={{
              border: "2px dashed #3F3F46",
              borderRadius: "12px",
              padding: "36px 20px",
              textAlign: "center",
              backgroundColor: "#0E0D0F",
            }}
          >
            <UploadCloud size={40} color="#FF9900" style={{ margin: "0 auto 12px auto" }} />
            <div style={{ fontSize: "14px", fontWeight: 600, color: "#FFFFFF", marginBottom: "6px" }}>
              Select CSV File to Import
            </div>
            <div style={{ fontSize: "12px", color: "#8E8E93", marginBottom: "16px" }}>
              Upload student roster in CSV format. Columns like Matric, Name, Level, Hall, and Email will be automatically detected.
            </div>
            <input
              type="file"
              accept=".csv"
              onChange={handleCsvFile}
              style={{ display: "none" }}
              id="csv-voter-input"
            />
            <label
              htmlFor="csv-voter-input"
              style={{
                display: "inline-block",
                padding: "8px 16px",
                borderRadius: "6px",
                backgroundColor: "#FF9900",
                color: "#171619",
                fontSize: "13px",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Browse File
            </label>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {/* File status toolbar */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "#0E0D0F",
                padding: "10px 14px",
                borderRadius: "8px",
                border: "1px solid #2D2C31",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <CheckCircle2 size={16} color="#10B981" />
                <span style={{ fontSize: "13px", fontWeight: 600, color: "#10B981" }}>
                  Parsed {csvRawRows.length} rows from "{csvFileName}"
                </span>
                <span style={{ fontSize: "11.5px", color: "#8E8E93" }}>
                  ({csvRawHeaders.length} columns detected)
                </span>
              </div>
              <button
                onClick={resetState}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#EF4444",
                  fontSize: "12px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Change File
              </button>
            </div>

            {/* Header Row Detection & Toggle */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "#1E1D21",
                padding: "10px 14px",
                borderRadius: "8px",
                border: "1px solid #2D2C31",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <input
                  type="checkbox"
                  id="has-header-toggle"
                  checked={hasHeaderRow}
                  onChange={(e) => handleToggleHeaders(e.target.checked)}
                  style={{ width: "16px", height: "16px", accentColor: "#FF9900", cursor: "pointer" }}
                />
                <label htmlFor="has-header-toggle" style={{ cursor: "pointer" }}>
                  <span style={{ fontSize: "12.5px", fontWeight: 700, color: "#FFFFFF" }}>
                    First row contains column headers
                  </span>
                  <span style={{ display: "block", fontSize: "11px", color: "#8E8E93" }}>
                    {hasHeaderRow
                      ? "Row 1 is treated as column titles (Matric, Name, Email, etc.)"
                      : "Row 1 is treated as an accredited student voter (e.g. Grace Shoile is included)"}
                  </span>
                </label>
              </div>
              <span
                style={{
                  fontSize: "11px",
                  padding: "3px 8px",
                  borderRadius: "4px",
                  backgroundColor: hasHeaderRow ? "rgba(59, 130, 246, 0.15)" : "rgba(255, 153, 0, 0.15)",
                  color: hasHeaderRow ? "#60A5FA" : "#FF9900",
                  fontWeight: 600,
                }}
              >
                {hasHeaderRow ? "Standard Headers" : "Headerless / Data Row 1"}
              </span>
            </div>

            {/* Column Import Mode Selection */}
            <div
              style={{
                backgroundColor: "#1E1D21",
                padding: "14px",
                borderRadius: "10px",
                border: "1px solid #2D2C31",
              }}
            >
              <div style={{ fontSize: "12.5px", fontWeight: 700, color: "#E4E4E7", marginBottom: "10px" }}>
                How would you like to import columns?
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "10px" }}>
                <button
                  type="button"
                  onClick={() => setCsvImportMode("all")}
                  style={{
                    padding: "10px 14px",
                    borderRadius: "8px",
                    border: csvImportMode === "all" ? "1.5px solid #FF9900" : "1px solid #2D2C31",
                    backgroundColor: csvImportMode === "all" ? "rgba(255, 153, 0, 0.1)" : "#0E0D0F",
                    color: csvImportMode === "all" ? "#FF9900" : "#A1A1AA",
                    textAlign: "left",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: "13px", marginBottom: "2px" }}>
                    Import All Matched Columns
                  </div>
                  <div style={{ fontSize: "11px", color: "#8E8E93" }}>
                    Automatically map detected columns (Matric, Name, Level, Hall, Gender, etc.)
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setCsvImportMode("custom")}
                  style={{
                    padding: "10px 14px",
                    borderRadius: "8px",
                    border: csvImportMode === "custom" ? "1.5px solid #FF9900" : "1px solid #2D2C31",
                    backgroundColor: csvImportMode === "custom" ? "rgba(255, 153, 0, 0.1)" : "#0E0D0F",
                    color: csvImportMode === "custom" ? "#FF9900" : "#A1A1AA",
                    textAlign: "left",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: "13px", marginBottom: "2px" }}>
                    Choose & Map Specific Columns
                  </div>
                  <div style={{ fontSize: "11px", color: "#8E8E93" }}>
                    Customize which attributes to import and specify exact source columns
                  </div>
                </button>
              </div>
            </div>

            {/* Custom Mapping Controls vs Auto-matched summary */}
            {csvImportMode === "custom" ? (
              <div
                style={{
                  backgroundColor: "#0E0D0F",
                  padding: "16px",
                  borderRadius: "10px",
                  border: "1px solid #2D2C31",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: "#A1A1AA",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    marginBottom: "12px",
                  }}
                >
                  Column Attribute Mapping
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "12px" }}>
                  {/* Matric */}
                  <div>
                    <label style={{ fontSize: "11.5px", fontWeight: 600, color: "#A1A1AA", display: "block", marginBottom: "4px" }}>
                      Matric Number *
                    </label>
                    <select
                      value={columnMapping.matric}
                      onChange={(e) => setColumnMapping({ ...columnMapping, matric: parseInt(e.target.value, 10) })}
                      style={{ width: "100%", padding: "7px 10px", borderRadius: "6px", backgroundColor: "#171619", border: "1px solid #3F3F46", color: "#FFFFFF", fontSize: "12px" }}
                    >
                      <option value={-1}>-- Not Selected --</option>
                      {csvRawHeaders.map((h, idx) => (
                        <option key={idx} value={idx}>Col {idx + 1}: {h}</option>
                      ))}
                    </select>
                  </div>

                  {/* Name */}
                  <div>
                    <label style={{ fontSize: "11.5px", fontWeight: 600, color: "#A1A1AA", display: "block", marginBottom: "4px" }}>
                      Student Full Name *
                    </label>
                    <select
                      value={columnMapping.name}
                      onChange={(e) => setColumnMapping({ ...columnMapping, name: parseInt(e.target.value, 10) })}
                      style={{ width: "100%", padding: "7px 10px", borderRadius: "6px", backgroundColor: "#171619", border: "1px solid #3F3F46", color: "#FFFFFF", fontSize: "12px" }}
                    >
                      <option value={-1}>-- Not Selected --</option>
                      {csvRawHeaders.map((h, idx) => (
                        <option key={idx} value={idx}>Col {idx + 1}: {h}</option>
                      ))}
                    </select>
                  </div>

                  {/* Email */}
                  <div>
                    <label style={{ fontSize: "11.5px", fontWeight: 600, color: "#A1A1AA", display: "block", marginBottom: "4px" }}>
                      Email Address
                    </label>
                    <select
                      value={columnMapping.email}
                      onChange={(e) => setColumnMapping({ ...columnMapping, email: parseInt(e.target.value, 10) })}
                      style={{ width: "100%", padding: "7px 10px", borderRadius: "6px", backgroundColor: "#171619", border: "1px solid #3F3F46", color: "#FFFFFF", fontSize: "12px" }}
                    >
                      <option value={-1}>-- Auto-generate (@student.ui.edu.ng) --</option>
                      {csvRawHeaders.map((h, idx) => (
                        <option key={idx} value={idx}>Col {idx + 1}: {h}</option>
                      ))}
                    </select>
                  </div>

                  {/* Password */}
                  <div>
                    <label style={{ fontSize: "11.5px", fontWeight: 600, color: "#A1A1AA", display: "block", marginBottom: "4px" }}>
                      Account Password
                    </label>
                    <select
                      value={columnMapping.password}
                      onChange={(e) => setColumnMapping({ ...columnMapping, password: parseInt(e.target.value, 10) })}
                      style={{ width: "100%", padding: "7px 10px", borderRadius: "6px", backgroundColor: "#171619", border: "1px solid #3F3F46", color: "#FFFFFF", fontSize: "12px" }}
                    >
                      <option value={-1}>-- Autopopulate (7-digit Mixed Random) --</option>
                      {csvRawHeaders.map((h, idx) => (
                        <option key={idx} value={idx}>Col {idx + 1}: {h}</option>
                      ))}
                    </select>
                  </div>

                  {/* Level */}
                  <div>
                    <label style={{ fontSize: "11.5px", fontWeight: 600, color: "#A1A1AA", display: "block", marginBottom: "4px" }}>
                      Academic Level
                    </label>
                    <select
                      value={columnMapping.level}
                      onChange={(e) => setColumnMapping({ ...columnMapping, level: parseInt(e.target.value, 10) })}
                      style={{ width: "100%", padding: "7px 10px", borderRadius: "6px", backgroundColor: "#171619", border: "1px solid #3F3F46", color: "#FFFFFF", fontSize: "12px" }}
                    >
                      <option value={-1}>-- Default (100L) --</option>
                      {csvRawHeaders.map((h, idx) => (
                        <option key={idx} value={idx}>Col {idx + 1}: {h}</option>
                      ))}
                    </select>
                  </div>

                  {/* Gender */}
                  <div>
                    <label style={{ fontSize: "11.5px", fontWeight: 600, color: "#A1A1AA", display: "block", marginBottom: "4px" }}>
                      Gender
                    </label>
                    <select
                      value={columnMapping.gender}
                      onChange={(e) => setColumnMapping({ ...columnMapping, gender: parseInt(e.target.value, 10) })}
                      style={{ width: "100%", padding: "7px 10px", borderRadius: "6px", backgroundColor: "#171619", border: "1px solid #3F3F46", color: "#FFFFFF", fontSize: "12px" }}
                    >
                      <option value={-1}>-- Not Collected / None --</option>
                      {csvRawHeaders.map((h, idx) => (
                        <option key={idx} value={idx}>Col {idx + 1}: {h}</option>
                      ))}
                    </select>
                  </div>

                  {/* Hall */}
                  <div>
                    <label style={{ fontSize: "11.5px", fontWeight: 600, color: "#A1A1AA", display: "block", marginBottom: "4px" }}>
                      Hall of Residence
                    </label>
                    <select
                      value={columnMapping.hall}
                      onChange={(e) => setColumnMapping({ ...columnMapping, hall: parseInt(e.target.value, 10) })}
                      style={{ width: "100%", padding: "7px 10px", borderRadius: "6px", backgroundColor: "#171619", border: "1px solid #3F3F46", color: "#FFFFFF", fontSize: "12px" }}
                    >
                      <option value={-1}>-- Not Collected / None --</option>
                      {csvRawHeaders.map((h, idx) => (
                        <option key={idx} value={idx}>Col {idx + 1}: {h}</option>
                      ))}
                    </select>
                  </div>

                  {/* Department */}
                  <div>
                    <label style={{ fontSize: "11.5px", fontWeight: 600, color: "#A1A1AA", display: "block", marginBottom: "4px" }}>
                      Department / Faculty
                    </label>
                    <select
                      value={columnMapping.department}
                      onChange={(e) => setColumnMapping({ ...columnMapping, department: parseInt(e.target.value, 10) })}
                      style={{ width: "100%", padding: "7px 10px", borderRadius: "6px", backgroundColor: "#171619", border: "1px solid #3F3F46", color: "#FFFFFF", fontSize: "12px" }}
                    >
                      <option value={-1}>-- Not Collected / None --</option>
                      {csvRawHeaders.map((h, idx) => (
                        <option key={idx} value={idx}>Col {idx + 1}: {h}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ) : (
              <div
                style={{
                  backgroundColor: "#0E0D0F",
                  padding: "14px 16px",
                  borderRadius: "10px",
                  border: "1px solid #2D2C31",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                  <span style={{ fontSize: "12px", fontWeight: 700, color: "#A1A1AA", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Auto-Detected Column Matches
                  </span>
                  <button
                    type="button"
                    onClick={() => setCsvImportMode("custom")}
                    style={{ background: "transparent", border: "none", color: "#FF9900", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}
                  >
                    Customize Specific Columns →
                  </button>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  <span style={{ padding: "4px 10px", borderRadius: "6px", backgroundColor: "#1E1D21", border: "1px solid #2D2C31", fontSize: "11.5px", color: columnMapping.matric !== -1 ? "#10B981" : "#EF4444" }}>
                    Matric: <b>{columnMapping.matric !== -1 ? csvRawHeaders[columnMapping.matric] : "Missing"}</b>
                  </span>
                  <span style={{ padding: "4px 10px", borderRadius: "6px", backgroundColor: "#1E1D21", border: "1px solid #2D2C31", fontSize: "11.5px", color: columnMapping.name !== -1 ? "#10B981" : "#EF4444" }}>
                    Name: <b>{columnMapping.name !== -1 ? csvRawHeaders[columnMapping.name] : "Missing"}</b>
                  </span>
                  <span style={{ padding: "4px 10px", borderRadius: "6px", backgroundColor: "#1E1D21", border: "1px solid #2D2C31", fontSize: "11.5px", color: "#A1A1AA" }}>
                    Email: <b>{columnMapping.email !== -1 ? csvRawHeaders[columnMapping.email] : "Auto-derive"}</b>
                  </span>
                  <span style={{ padding: "4px 10px", borderRadius: "6px", backgroundColor: "#1E1D21", border: "1px solid #2D2C31", fontSize: "11.5px", color: "#A1A1AA" }}>
                    Level: <b>{columnMapping.level !== -1 ? csvRawHeaders[columnMapping.level] : "Default (100L)"}</b>
                  </span>
                  <span style={{ padding: "4px 10px", borderRadius: "6px", backgroundColor: "#1E1D21", border: "1px solid #2D2C31", fontSize: "11.5px", color: "#A1A1AA" }}>
                    Gender: <b>{columnMapping.gender !== -1 ? csvRawHeaders[columnMapping.gender] : "Default"}</b>
                  </span>
                  <span style={{ padding: "4px 10px", borderRadius: "6px", backgroundColor: "#1E1D21", border: "1px solid #2D2C31", fontSize: "11.5px", color: "#A1A1AA" }}>
                    Hall: <b>{columnMapping.hall !== -1 ? csvRawHeaders[columnMapping.hall] : "Default"}</b>
                  </span>
                </div>
              </div>
            )}

            {/* Password Autopopulation Callout & Checkbox */}
            <div
              style={{
                backgroundColor: "rgba(255, 153, 0, 0.08)",
                border: "1px solid rgba(255, 153, 0, 0.25)",
                borderRadius: "10px",
                padding: "12px 16px",
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
              }}
            >
              <input
                type="checkbox"
                id="autogen-pwd-toggle"
                checked={autoGenPasswords}
                onChange={(e) => setAutoGenPasswords(e.target.checked)}
                style={{ marginTop: "3px", width: "16px", height: "16px", accentColor: "#FF9900", cursor: "pointer" }}
              />
              <label htmlFor="autogen-pwd-toggle" style={{ fontSize: "12.5px", cursor: "pointer" }}>
                <span style={{ fontWeight: 700, color: "#FF9900", display: "block", marginBottom: "2px" }}>
                  Autopopulate Randomised 7-Digit Passwords
                </span>
                <span style={{ color: "#D4D4D8", lineHeight: 1.4 }}>
                  Generates secure 7-digit mixed uppercase & number passwords (excluding look-alikes like 0/O, 1/I/L) for all imported accounts.
                </span>
              </label>
            </div>

            {/* Duplicate Detection Alert & Drilldown */}
            {totalDuplicatesDropped > 0 && (
              <div
                style={{
                  backgroundColor: "rgba(245, 158, 11, 0.08)",
                  border: "1px solid rgba(245, 158, 11, 0.3)",
                  borderRadius: "10px",
                  padding: "12px 16px",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "10px", flexWrap: "wrap" }}>
                  <div style={{ display: "flex", gap: "10px", flex: 1, minWidth: "260px" }}>
                    <AlertTriangle size={18} color="#F59E0B" style={{ marginTop: "2px", flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: "13px", fontWeight: 700, color: "#F59E0B", marginBottom: "2px" }}>
                        {totalDuplicatesDropped} Duplicate {totalDuplicatesDropped === 1 ? "Record" : "Records"} Detected ({uniqueVoters.length} Unique Voters)
                      </div>
                      <div style={{ fontSize: "12px", color: "#D4D4D8", lineHeight: 1.4 }}>
                        Your CSV has {csvParsedVoters.length} entries, but {totalDuplicatesDropped} {totalDuplicatesDropped === 1 ? "row shares" : "rows share"} duplicate matric numbers (e.g. repeated dues payments or re-submissions). Electpoll accredits each student exactly once to guarantee one vote per person.
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowDuplicateDrawer(!showDuplicateDrawer)}
                    style={{
                      background: "rgba(245, 158, 11, 0.15)",
                      border: "1px solid rgba(245, 158, 11, 0.4)",
                      color: "#FCD34D",
                      fontSize: "11.5px",
                      fontWeight: 700,
                      padding: "5px 10px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {showDuplicateDrawer ? "Hide Duplicate Details" : `View ${duplicateGroups.length} Duplicated Matrics`}
                  </button>
                </div>

                {showDuplicateDrawer && (
                  <div
                    style={{
                      marginTop: "12px",
                      paddingTop: "12px",
                      borderTop: "1px solid rgba(245, 158, 11, 0.2)",
                      maxHeight: "180px",
                      overflowY: "auto",
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                    }}
                  >
                    <div style={{ fontSize: "11px", fontWeight: 700, color: "#A1A1AA", textTransform: "uppercase", marginBottom: "2px" }}>
                      Repeated Matric Numbers:
                    </div>
                    {duplicateGroups.map((group, gIdx) => (
                      <div
                        key={gIdx}
                        style={{
                          fontSize: "11.5px",
                          backgroundColor: "#171619",
                          padding: "6px 10px",
                          borderRadius: "6px",
                          border: "1px solid #2D2C31",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          <span style={{ color: "#FF9900", fontWeight: 700, fontFamily: "monospace", marginRight: "8px" }}>
                            {group.matric.toUpperCase()}
                          </span>
                          <span style={{ color: "#E4E4E7" }}>
                            {group.rows.map((r) => `${r.name} (Row ${r.rowIdx})`).join(" • ")}
                          </span>
                        </div>
                        <span style={{ color: "#F59E0B", fontWeight: 700, fontSize: "11px", whiteSpace: "nowrap" }}>
                          {group.count}x entries
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Live Preview Table */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                <span style={{ fontSize: "12px", fontWeight: 700, color: "#8E8E93", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Preview of Accredited Voters (Showing {Math.min(5, uniqueVoters.length)} of {uniqueVoters.length} Unique Voters)
                </span>
                {columnMapping.matric === -1 || columnMapping.name === -1 ? (
                  <span style={{ fontSize: "11.5px", color: "#EF4444", fontWeight: 600 }}>
                    ⚠️ Matric Number and Name must be mapped
                  </span>
                ) : null}
              </div>

              <div style={{ maxHeight: "180px", overflowY: "auto", border: "1px solid #2D2C31", borderRadius: "8px" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#1E1D21", color: "#8E8E93", textAlign: "left" }}>
                      <th style={{ padding: "8px 12px" }}>Matric</th>
                      <th style={{ padding: "8px 12px" }}>Name</th>
                      <th style={{ padding: "8px 12px" }}>Password</th>
                      <th style={{ padding: "8px 12px" }}>Level</th>
                      <th style={{ padding: "8px 12px" }}>Gender</th>
                      <th style={{ padding: "8px 12px" }}>Hall</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uniqueVoters.slice(0, 5).map((row, idx) => (
                      <tr key={idx} style={{ borderBottom: "1px solid #222126" }}>
                        <td style={{ padding: "8px 12px", color: "#FF9900", fontWeight: 600 }}>{row.matric}</td>
                        <td style={{ padding: "8px 12px", fontWeight: 600 }}>{row.name}</td>
                        <td style={{ padding: "8px 12px" }}>
                          <span
                            style={{
                              fontFamily: "monospace",
                              fontWeight: 700,
                              color: "#F4F4F5",
                              backgroundColor: "#232227",
                              border: "1px solid #3A3940",
                              padding: "2px 6px",
                              borderRadius: "4px",
                            }}
                          >
                            {row.password}
                          </span>
                        </td>
                        <td style={{ padding: "8px 12px" }}>{row.level}L</td>
                        <td style={{ padding: "8px 12px", color: "#8E8E93" }}>{row.gender}</td>
                        <td style={{ padding: "8px 12px", color: "#8E8E93" }}>{row.hall}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        )}
      </div>

      {/* Sticky Bottom Action Footer */}
      {csvRawRows.length > 0 && (
        <div
          style={{
            padding: "16px 28px",
            borderTop: "1px solid #2D2C31",
            backgroundColor: "#131215",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            flexShrink: 0,
          }}
        >
          {importError && (
            <div
              style={{
                backgroundColor: "rgba(239, 68, 68, 0.12)",
                border: "1px solid rgba(239, 68, 68, 0.35)",
                borderRadius: "8px",
                padding: "10px 14px",
                color: "#FCA5A5",
                fontSize: "12.5px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <AlertTriangle size={16} color="#EF4444" style={{ flexShrink: 0 }} />
              <span>{importError}</span>
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
            <div style={{ fontSize: "12.5px", color: "#A1A1AA" }}>
              {columnMapping.matric === -1 || columnMapping.name === -1 ? (
                <span style={{ color: "#EF4444", fontWeight: 600 }}>⚠️ Please map Matric Number and Full Name</span>
              ) : (
                <span>Ready to enroll <b>{uniqueVoters.length}</b> accredited student voters</span>
              )}
            </div>

            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => downloadBatchCsv(uniqueVoters, `prepared-voters-list-${uniqueVoters.length}.csv`)}
                disabled={uniqueVoters.length === 0}
                title="Download CSV of this batch with generated passwords before committing"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "10px 14px",
                  borderRadius: "6px",
                  backgroundColor: "#201F24",
                  color: "#D4D4D8",
                  border: "1px solid #3F3F46",
                  fontSize: "12.5px",
                  fontWeight: 600,
                  cursor: uniqueVoters.length === 0 ? "not-allowed" : "pointer",
                }}
              >
                <Download size={14} /> Export Prepared List ({uniqueVoters.length})
              </button>

              <button
                type="button"
                onClick={handleClose}
                disabled={importingVoters}
                style={{
                  padding: "10px 16px",
                  borderRadius: "6px",
                  backgroundColor: "#27272A",
                  color: "#E4E4E7",
                  border: "1px solid #3F3F46",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>

              <button
                disabled={importingVoters || uniqueVoters.length === 0 || columnMapping.matric === -1 || columnMapping.name === -1}
                onClick={handleCommitCsv}
                style={{
                  padding: "10px 22px",
                  borderRadius: "6px",
                  backgroundColor: uniqueVoters.length === 0 || columnMapping.matric === -1 || columnMapping.name === -1 ? "#3F3F46" : "#FF9900",
                  color: uniqueVoters.length === 0 || columnMapping.matric === -1 || columnMapping.name === -1 ? "#8E8E93" : "#171619",
                  border: "none",
                  fontWeight: 800,
                  fontSize: "13px",
                  cursor: uniqueVoters.length === 0 || columnMapping.matric === -1 || columnMapping.name === -1 ? "not-allowed" : "pointer",
                  transition: "all 0.15s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                {importingVoters
                  ? (importProgress && importProgress.total > 0
                      ? `Adding Voters (${importProgress.current} of ${importProgress.total})...`
                      : "Adding Voters into Database...")
                  : totalDuplicatesDropped > 0
                  ? `Add & Accredit ${uniqueVoters.length} Voters (${totalDuplicatesDropped} dupes skipped)`
                  : `Add & Accredit All ${uniqueVoters.length} Voters`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};
